import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiArrowRight } from '@mdi/js'
import { useLocale } from '../../contexts/LocaleContext'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import {
    useGroupMap,
    useGroupMatrix,
    useGroupSummary,
    useGroupTerritories,
    useGroupTrend,
    type GroupFamilySummary,
    type GroupKPIMeta,
    type Level,
    type Period,
} from '../../api/groupSteering'
import { formatUnit, langOf, text } from './format'
import { FourFigures, KpiPills, KpiTile, ReadingBlock, Section } from './primitives'
import { ConcentrationList, OutOfTargetBars, TrendChart, VolumeValueScatter } from './charts'
import { PortfolioMap, TerritoryTable } from './PortfolioMap'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * The consolidated view of one family — the same structure for the seven:
 * title and question, family facets, tiles, the sections (trend, footprint,
 * out of target, concentration, volume × value), two exit cards.
 */
export function ConsolidatedView({
    org,
    period,
    level,
    family,
    kpi,
    onFamilyChange,
    onKpiChange,
}: {
    org: string
    period: Period
    level: Level
    family: string | undefined
    kpi: string | undefined
    onFamilyChange: (family: string) => void
    onKpiChange: (kpi: string | undefined) => void
}) {
    const { t } = useTranslation('group-steering')
    const { t: tLeases } = useTranslation('leases')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const summary = useGroupSummary(org, period, level)
    const families = useMemo(() => (summary.data?.families ?? []).filter((f) => f.tiles.length > 0), [summary.data])
    const activeFamily: GroupFamilySummary | undefined = families.find((f) => f.code === family) ?? families[0]
    const tiles = activeFamily?.tiles ?? []
    const activeKpi = tiles.find((tl) => tl.kpi.code === kpi) ?? tiles[0]
    const matrix = useGroupMatrix(org, period, level, activeFamily?.code)
    const trend = useGroupTrend(org, activeKpi?.kpi.code, level)
    const map = useGroupMap(org, activeKpi?.kpi.code, period)
    const territories = useGroupTerritories(org, activeKpi?.kpi.code, period)
    const [trendView, setTrendView] = useState<'chart' | 'table'>('chart')
    const [mapView, setMapView] = useState<'chart' | 'table'>('chart')
    const [outView, setOutView] = useState<'chart' | 'table'>('chart')

    if (summary.isLoading) {
        return (
            <div className="space-y-3" data-testid="group-consolidated-loading">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-40 w-full" />
            </div>
        )
    }
    if (!activeFamily || !activeKpi) return null
    const kpiMeta: GroupKPIMeta = activeKpi.kpi
    const rows = matrix.data?.rows ?? []
    const kpiMetas = tiles.map((tl) => tl.kpi)
    const lastMonth = trend.data?.months[trend.data.months.length - 1]
    const trendCsv = () =>
        ['month,pooled,n,peers_p50,target', ...(trend.data?.months ?? []).map((m) => [m.month, m.pooled ?? '', m.n, m.peers_p50 ?? '', m.target ?? ''].join(','))].join('\n')
    const outCsv = () => ['perimeter,value,n,tier', ...rows.map((r) => [r.perimeter.name ?? '', r.values[kpiMeta.code]?.value ?? '', r.values[kpiMeta.code]?.n ?? '', r.values[kpiMeta.code]?.tier ?? ''].join(','))].join('\n')

    return (
        <div className="space-y-4" data-testid="group-consolidated">
            <PageHeader
                icon={SECTION_ICONS.group_steering}
                title={text(activeFamily.label, lang)}
                description={
                    <>
                        {text(activeFamily.question, lang)}
                        <span className="mt-1 block text-xs">{t('consolidated.intro')}</span>
                    </>
                }
                data-testid="group-family-title"
            />

            {/* Family facets — pattern §21, server counts of perimeters in exception. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="group-family-facets">
                {families.map((f) => (
                    <button
                        key={f.code}
                        type="button"
                        onClick={() => onFamilyChange(f.code)}
                        aria-pressed={f.code === activeFamily.code}
                        className={`text-left border rounded-lg p-2.5 transition-colors ${f.code === activeFamily.code ? 'bg-primary/5 border-primary ring-2 ring-primary/30' : 'hover:bg-muted/50'}`}
                        data-testid={`group-family-facet-${f.code}`}
                    >
                        <div className="text-sm font-medium text-foreground truncate">{text(f.label, lang)}</div>
                        <div className={`text-[11px] ${f.exceptions_count > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                            {f.exceptions_count > 0 ? t('families.exceptions', { count: f.exceptions_count }) : t('families.none')}
                        </div>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="group-tiles">
                {tiles.map((tl) => (
                    <KpiTile key={tl.kpi.code} kpi={tl.kpi} cell={tl.pooled} outOfTarget={tl.out_of_target} peers={tl.peers} selected={tl.kpi.code === kpiMeta.code} onSelect={() => onKpiChange(tl.kpi.code)} />
                ))}
            </div>

            <Section
                id={`trend-${activeFamily.code}`}
                title={t('consolidated.section_trend', { kpi: text(kpiMeta.label, lang) })}
                badge={<Badge variant="outline" className="tabular-nums">{activeKpi.pooled.suppressed ? '—' : formatUnit(activeKpi.pooled.value, kpiMeta.unit, lang, locale)}</Badge>}
                description={t('consolidated.section_trend_desc')}
                view={trendView}
                onViewChange={setTrendView}
                csv={trendCsv}
            >
                <FourFigures kpi={kpiMeta} pooled={activeKpi.pooled} peers={activeKpi.peers} outOfTarget={activeKpi.out_of_target} trend={activeKpi.pooled.delta_vs_previous} />
                <KpiPills kpis={kpiMetas} value={kpiMeta.code} onChange={(c) => onKpiChange(c)} lang={lang} />
                {trend.data ? (
                    trendView === 'chart' ? (
                        <TrendChart trend={trend.data} kpi={kpiMeta} />
                    ) : (
                        <TrendTable months={trend.data.months} kpi={kpiMeta} />
                    )
                ) : (
                    <Skeleton className="h-[260px] w-full" />
                )}
                {lastMonth && lastMonth.pooled !== null && lastMonth.pooled !== undefined && (
                    <p className="text-sm text-foreground" data-testid="trend-last-month">
                        {t('consolidated.last_month', { value: formatUnit(lastMonth.pooled, kpiMeta.unit, lang, locale), target: formatUnit(kpiMeta.target.target, kpiMeta.unit, lang, locale) })}
                    </p>
                )}
                {trend.data?.history_since && (
                    <p className="text-[11px] text-muted-foreground">{t('space.history_since', { date: new Date(trend.data.history_since).toLocaleDateString(locale) })}</p>
                )}
                <ReadingBlock reading={trend.data?.reading ?? activeKpi.reading} />
            </Section>

            <Section
                id={`map-${activeFamily.code}`}
                title={t('consolidated.section_map')}
                badge={
                    map.data ? (
                        <Badge variant="outline" className="tabular-nums">
                            {t('consolidated.map_sites', { count: map.data.sites })}
                            {map.data.not_geocoded > 0 ? ` · ${t('consolidated.map_not_geocoded', { count: map.data.not_geocoded })}` : ''}
                        </Badge>
                    ) : null
                }
                description={t('consolidated.section_map_desc')}
                view={mapView}
                onViewChange={setMapView}
            >
                {mapView === 'chart' ? (
                    map.data ? <PortfolioMap points={map.data.points} kpi={kpiMeta} org={org} /> : <Skeleton className="h-[360px] w-full" />
                ) : territories.data ? (
                    <TerritoryTable territories={territories.data} kpi={kpiMeta} />
                ) : (
                    <Skeleton className="h-40 w-full" />
                )}
                {territories.data && mapView === 'chart' && territories.data.rows.length > 0 && (
                    <TerritoryTable territories={territories.data} kpi={kpiMeta} />
                )}
                <ReadingBlock reading={activeKpi.reading} />
            </Section>

            <Section
                id={`out-${activeFamily.code}`}
                title={t('consolidated.section_out')}
                badge={<Badge variant="outline" className="tabular-nums">{activeKpi.out_of_target.count} / {activeKpi.out_of_target.total}</Badge>}
                description={t('consolidated.section_out_desc')}
                view={outView}
                onViewChange={setOutView}
                csv={outCsv}
            >
                {rows.some((r) => r.values[kpiMeta.code]?.out_of_target) ? (
                    outView === 'chart' ? (
                        <OutOfTargetBars rows={rows} kpi={kpiMeta} />
                    ) : (
                        <OutOfTargetTable rows={rows} kpi={kpiMeta} />
                    )
                ) : (
                    <p className="text-sm text-muted-foreground" data-testid="no-out-of-target">
                        {t('consolidated.no_out_of_target')}
                    </p>
                )}
                {matrix.data?.readings[kpiMeta.code] && <ReadingBlock reading={matrix.data.readings[kpiMeta.code]} />}
            </Section>

            <Section id={`concentration-${activeFamily.code}`} title={t('consolidated.section_concentration')} description={t('consolidated.section_concentration_desc')} defaultOpen={false}>
                <ConcentrationList rows={rows} kpi={kpiMeta} peersP50={matrix.data?.peers[kpiMeta.code]?.p50} />
            </Section>

            <Section id={`scatter-${activeFamily.code}`} title={t('consolidated.section_scatter')} description={t('consolidated.section_scatter_desc')} defaultOpen={false}>
                {rows.length > 0 ? <VolumeValueScatter rows={rows} kpi={kpiMeta} /> : <Skeleton className="h-40 w-full" />}
            </Section>

            <div className="grid md:grid-cols-2 gap-3" data-testid="group-exit-cards">
                <Link to="/$org/group/perimeters" params={{ org }} search={{ period, level, family: activeFamily.code, kpi: kpiMeta.code, selected: undefined, q: undefined }} className="rounded-lg border p-4 hover:bg-muted/40" data-testid="group-exit-perimeters">
                    <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{t('consolidated.exit_perimeters')}</span>
                        <AppIcon tone="muted" path={mdiArrowRight} size="sm" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('consolidated.exit_perimeters_desc')}</p>
                </Link>
                {activeFamily.code === 'rental' && (
                    <Link
                        to="/$org/leases"
                        params={{ org }}
                        search={{ under: undefined, regime: undefined, ends_before: undefined, status: undefined, page: undefined }}
                        className="rounded-lg border p-4 hover:bg-muted/40"
                        data-testid="group-exit-leases"
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground">{text(activeFamily.default_action, lang)}</span>
                            <AppIcon tone="muted" path={mdiArrowRight} size="sm" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{tLeases('steering_link.description')}</p>
                    </Link>
                )}
                {summary.data?.profile === 'group' ? (
                    <Link to="/$org/group/governance" params={{ org }} search={{ period }} className="rounded-lg border p-4 hover:bg-muted/40" data-testid="group-exit-governance">
                        <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground">{t('consolidated.exit_governance')}</span>
                            <AppIcon tone="muted" path={mdiArrowRight} size="sm" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{t('consolidated.exit_governance_desc')}</p>
                    </Link>
                ) : (
                    <Link to="/$org/group/criteria" params={{ org }} className="rounded-lg border p-4 hover:bg-muted/40" data-testid="group-exit-criteria">
                        <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground">{t('consolidated.exit_criteria')}</span>
                            <AppIcon tone="muted" path={mdiArrowRight} size="sm" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{t('consolidated.exit_criteria_desc')}</p>
                    </Link>
                )}
            </div>
        </div>
    )
}

function TrendTable({ months, kpi }: { months: { month: string; pooled?: number | null; n: number; peers_p50?: number; target?: number }[]; kpi: GroupKPIMeta }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trend-table">
                <thead className="text-xs text-muted-foreground">
                    <tr className="text-left">
                        <th className="py-1 pr-2 font-medium">—</th>
                        <th className="py-1 pr-2 font-medium text-right">{t('consolidated.summary_pooled')}</th>
                        <th className="py-1 pr-2 font-medium text-right">n</th>
                        <th className="py-1 pr-2 font-medium text-right">{t('consolidated.summary_peers')}</th>
                        <th className="py-1 font-medium text-right">{t('consolidated.target', { value: '' })}</th>
                    </tr>
                </thead>
                <tbody>
                    {months.map((m) => (
                        <tr key={m.month} className="border-t">
                            <td className="py-1 pr-2 font-mono text-xs">{m.month}</td>
                            <td className="py-1 pr-2 text-right tabular-nums">{formatUnit(m.pooled, kpi.unit, lang, locale)}</td>
                            <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">{m.n}</td>
                            <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">{formatUnit(m.peers_p50, kpi.unit, lang, locale)}</td>
                            <td className="py-1 text-right tabular-nums text-muted-foreground">{formatUnit(m.target, kpi.unit, lang, locale)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function OutOfTargetTable({ rows, kpi }: { rows: { perimeter: { name?: string; anonymous?: boolean }; values: Record<string, { value?: number | null; n: number; tier: string; out_of_target: boolean }> }[]; kpi: GroupKPIMeta }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="out-of-target-table">
                <thead className="text-xs text-muted-foreground">
                    <tr className="text-left">
                        <th className="py-1 pr-2 font-medium">{t('exceptions.perimeter')}</th>
                        <th className="py-1 pr-2 font-medium text-right">{t('exceptions.value')}</th>
                        <th className="py-1 pr-2 font-medium text-right">n</th>
                        <th className="py-1 font-medium">{t('tier.none')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows
                        .filter((r) => r.values[kpi.code]?.out_of_target)
                        .map((r, i) => (
                            <tr key={i} className="border-t">
                                <td className="py-1 pr-2">{r.perimeter.anonymous ? '…' : r.perimeter.name}</td>
                                <td className="py-1 pr-2 text-right tabular-nums">{formatUnit(r.values[kpi.code]?.value, kpi.unit, lang, locale)}</td>
                                <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">{r.values[kpi.code]?.n}</td>
                                <td className="py-1">{t(`tier.${r.values[kpi.code]?.tier ?? 'none'}`)}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    )
}
